import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationItem from "components/common/NotificationItem";
import { Notification } from "components/common/NotificationTypes";
import { useSavedNotifications } from "hooks/useSavedNotifications";
import { Bell, CheckCheck, Inbox, X } from "lucide-react";
import { useSuccessModal } from "hooks/useSuccessModal";
import "assets/css/notifications.css";
import './NotificationsDrawer.css';

export type NotificationCategoryTab = "All" | "Applications" | "Credits" | "System";

export interface NotificationsDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  showCount?: number;
  onViewAll?: () => void;
  className?: string;
  defaultTab?: NotificationCategoryTab;
}

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({
  isOpen = true,
  onClose,
  showCount,
  onViewAll,
  className = "",
  defaultTab = "All",
}) => {
  const navigate = useNavigate();
  const showSuccess = useSuccessModal();
  const [activeTab, setActiveTab] = useState<NotificationCategoryTab>(defaultTab);

  // Fetch notifications from backend with category filter applied
  const {
    notifications,
    unreadCount,
    categoryCounts: backendCategoryCounts,
    loading,
    error,
    markRead,
    refetch,
  } = useSavedNotifications({
    pageSize: 50,
    category: activeTab !== "All" ? activeTab.toLowerCase() : undefined,
  });

  // Categorize notifications for local state filtering and count badges
  const categorizeNotification = useCallback((n: Notification): NotificationCategoryTab => {
    const type = n.type || "";
    const rawType = (n.rawType || "").toLowerCase();
    const content = (n.content || "").toLowerCase();
    const detail = (n.detail || "").toLowerCase();

    if (
      type === "job_application" ||
      type === "job_post" ||
      rawType.includes("job") ||
      rawType.includes("application") ||
      content.includes("job") ||
      content.includes("application") ||
      content.includes("applied") ||
      detail.includes("job") ||
      detail.includes("application")
    ) {
      return "Applications";
    }

    if (
      type === "subscription" ||
      rawType.includes("credit") ||
      rawType.includes("payment") ||
      rawType.includes("wallet") ||
      content.includes("credit") ||
      content.includes("coin") ||
      content.includes("balance") ||
      content.includes("payment") ||
      content.includes("deduct") ||
      content.includes("subscription") ||
      detail.includes("credit")
    ) {
      return "Credits";
    }

    return "System";
  }, []);

  // Filter local state based on active category tab
  const filteredNotifications = useMemo(() => {
    let list = notifications;
    if (activeTab !== "All") {
      list = notifications.filter((n) => categorizeNotification(n) === activeTab);
    }
    if (showCount && showCount > 0) {
      return list.slice(0, showCount);
    }
    return list;
  }, [notifications, activeTab, categorizeNotification, showCount]);

  // Compute category counts for tab badges
  const categoryCounts = useMemo(() => {
    if (
      backendCategoryCounts &&
      (backendCategoryCounts.all > 0 ||
        backendCategoryCounts.applications > 0 ||
        backendCategoryCounts.credits > 0 ||
        backendCategoryCounts.system > 0)
    ) {
      return {
        All: backendCategoryCounts.all,
        Applications: backendCategoryCounts.applications,
        Credits: backendCategoryCounts.credits,
        System: backendCategoryCounts.system,
      };
    }
    const counts: Record<NotificationCategoryTab, number> = {
      All: notifications.length,
      Applications: 0,
      Credits: 0,
      System: 0,
    };
    notifications.forEach((n) => {
      const cat = categorizeNotification(n);
      if (counts[cat] !== undefined) {
        counts[cat]++;
      }
    });
    return counts;
  }, [backendCategoryCounts, notifications, categorizeNotification]);

  const handleTabSelect = (tab: NotificationCategoryTab) => {
    setActiveTab(tab);
  };

  const handleMarkAllRead = async () => {
    await markRead(0);
    showSuccess({
      title: "All Caught Up!",
      message: "All notifications have been marked as read.",
      buttonText: "Done",
    });
  };

  const handleNavigate = (url: string) => {
    if (onClose) onClose();
    navigate(url);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`notifications-drawer-panel bg-white shadow-lg border ${className}`}
      style={{ zIndex: 1050 }}
      data-testid="notifications-drawer"
    >
      {/* Drawer Header */}
      <div className="notifications-drawer-header d-flex align-items-center justify-content-between border-bottom">
        <div className="d-flex align-items-center gap-2">
          <div className="notifications-drawer-icon-wrap rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center">
            <Bell size={18} />
          </div>
          <div>
            <h5 className="notifications-drawer-title m-0 fw-semibold">Notifications</h5>
            <small className="notifications-drawer-subtitle text-muted">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              className="notifications-drawer-mark-read text-nowrap"
              onClick={handleMarkAllRead}
            >
              <CheckCheck size={12} />
              Mark read
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="notifications-drawer-close-btn btn btn-sm btn-icon text-secondary p-1"
              onClick={onClose}
              aria-label="Close notifications drawer"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Tabbed Filter Bar (All, Applications, Credits, System) */}
      <div
        className="notifications-tab-bar d-flex gap-1 bg-light rounded-3 border overflow-auto"
        role="tablist"
        aria-label="Notification Categories"
      >
        {(["All", "Applications", "Credits", "System"] as NotificationCategoryTab[]).map((tab) => {
          const isActive = activeTab === tab;
          const count = categoryCounts[tab];
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`notifications-tab-btn btn btn-sm flex-fill text-nowrap rounded-2 transition-all ${
                isActive ? "bg-white text-primary shadow-sm fw-semibold" : "text-secondary hover-bg-white"
              }`}
              onClick={() => handleTabSelect(tab)}
            >
              {tab}
              <span
                className={`notifications-tab-badge ms-1 badge rounded-pill ${
                  isActive ? "bg-primary text-white" : "bg-secondary bg-opacity-25 text-secondary"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Notification List Body */}
      <div className="notifications-drawer-body overflow-auto">
        {loading && (
          <div className="notifications-drawer-state py-3 text-center text-muted">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            Loading {activeTab !== "All" ? activeTab.toLowerCase() : ""} notifications...
          </div>
        )}

        {!loading && error && (
          <div className="notifications-drawer-state py-3 text-center text-danger">
            {error}
          </div>
        )}

        {!loading && !error && filteredNotifications.length === 0 && (
          <div className="notifications-drawer-state py-3 text-center text-muted">
            <Inbox size={22} className="mb-2 text-secondary opacity-50" />
            <p className="m-0">No {activeTab !== "All" ? activeTab.toLowerCase() : ""} notifications found.</p>
          </div>
        )}

        {!loading && !error && filteredNotifications.map((n) => (
          <div key={n.id} className="border-bottom last-border-0">
            <NotificationItem
              notification={n}
              onRead={(id) => markRead(id)}
              onNavigate={handleNavigate}
            />
          </div>
        ))}
      </div>

      {/* Footer */}
      {(onViewAll || !showCount) && (
        <div className="notifications-drawer-footer text-center border-top">
          <button
            type="button"
            className="notifications-drawer-footer-btn btn btn-link text-decoration-none text-dark fw-medium p-0"
            onClick={() => {
              if (onClose) onClose();
              if (onViewAll) onViewAll();
              else navigate("/notifications");
            }}
          >
            View All Notifications &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationsDrawer;
